/**
 * Enterprise-grade Toast Notification System
 * Provides beautiful, animated toast notifications with multiple types
 */

import { reactive, createApp, h } from 'vue'
import ToastContainer from '@/components/notifications/ToastContainer.vue'

// Toast state management
const state = reactive({
  toasts: [],
  maxToasts: 3,
  defaultDuration: 3000
})

let toastId = 0
let containerMounted = false

// Toast types with their configurations
const TOAST_TYPES = {
  success: {
    icon: 'check-circle',
    bgClass: 'toast-success',
    title: 'Success'
  },
  error: {
    icon: 'x-circle',
    bgClass: 'toast-error',
    title: 'Error'
  },
  warning: {
    icon: 'alert-triangle',
    bgClass: 'toast-warning',
    title: 'Warning'
  },
  info: {
    icon: 'info',
    bgClass: 'toast-info',
    title: 'Info'
  }
}

/**
 * Mount the toast container to the DOM
 */
function mountContainer() {
  if (containerMounted) return
  
  const container = document.createElement('div')
  container.id = 'toast-container-root'
  document.body.appendChild(container)
  
  const app = createApp({
    render() {
      return h(ToastContainer, { toasts: state.toasts })
    }
  })
  
  app.mount(container)
  containerMounted = true
}

/**
 * Add a toast notification
 */
function addToast(options) {
  mountContainer()
  
  const id = ++toastId
  const type = options.type || 'info'
  const typeConfig = TOAST_TYPES[type] || TOAST_TYPES.info
  
  const toast = {
    id,
    type,
    title: options.title || typeConfig.title,
    message: options.message || '',
    icon: options.icon || typeConfig.icon,
    bgClass: typeConfig.bgClass,
    duration: options.duration ?? state.defaultDuration,
    closable: options.closable === true,
    action: options.action || null,
    progress: options.progress === true,
    createdAt: Date.now()
  }
  
  // Limit max toasts
  if (state.toasts.length >= state.maxToasts) {
    state.toasts.shift()
  }
  
  state.toasts.push(toast)
  
  // Auto remove after duration
  if (toast.duration > 0) {
    setTimeout(() => {
      removeToast(id)
    }, toast.duration)
  }
  
  return id
}

/**
 * Remove a toast by ID
 */
function removeToast(id) {
  const index = state.toasts.findIndex(t => t.id === id)
  if (index > -1) {
    state.toasts.splice(index, 1)
  }
}

/**
 * Clear all toasts
 */
function clearAll() {
  state.toasts.splice(0, state.toasts.length)
}

// Toast API
const toast = {
  success(message, options = {}) {
    return addToast({ ...options, type: 'success', message })
  },
  
  error(message, options = {}) {
    return addToast({ ...options, type: 'error', message, duration: options.duration ?? 4000 })
  },
  
  warning(message, options = {}) {
    return addToast({ ...options, type: 'warning', message })
  },
  
  info(message, options = {}) {
    return addToast({ ...options, type: 'info', message })
  },
  
  custom(options) {
    return addToast(options)
  },
  
  remove: removeToast,
  clearAll,
  
  // Promise-based toast for async operations
  promise(promise, { loading, success, error }) {
    const id = addToast({
      type: 'info',
      message: loading || 'Loading...',
      duration: 0,
      progress: false
    })
    
    return promise
      .then(result => {
        removeToast(id)
        toast.success(typeof success === 'function' ? success(result) : success)
        return result
      })
      .catch(err => {
        removeToast(id)
        toast.error(typeof error === 'function' ? error(err) : error)
        throw err
      })
  }
}

// Vue plugin
export const ToastPlugin = {
  install(app) {
    app.config.globalProperties.$toast = toast
    app.provide('toast', toast)
  }
}

// Export for composition API
export function useToast() {
  return toast
}

export default toast
